import { Card, Divider, Switch, Typography, Row, Col } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react"; // Anta at dette er en custom message-komponent
import { message } from "@bindeleddet/core/utils";
import { Company } from "@bindeleddet/core/types";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

interface Props {
  company: Company;
}

const { Text } = Typography;

const InformationSwitch = ({
  label,
  checked,
  onChange,
  loading,
}: {
  label: string;
  checked: boolean;
  onChange: (newState: boolean) => void;
  loading: boolean;
}) => {
  return (
    <Row align="middle" style={{ marginBottom: 12 }}>
      <Col span={16}>
        <Text>{label}</Text>
      </Col>
      <Col span={8} style={{ textAlign: "right" }}>
        <Switch
          checked={checked}
          onChange={onChange}
          loading={loading}
          checkedChildren={<CheckOutlined />}
          unCheckedChildren={<CloseOutlined />}
          style={{ backgroundColor: !checked ? "#ff4d4f" : "var(--primary-color" }}
        />
      </Col>
    </Row>
  );
};

const DetailedInformation: React.FC<Props> = ({ company }) => {
  const [isRelevant, setIsRelevant] = useState<boolean>(company.is_relevant);
  const [isRelevantLoading, setIsRelevantLoading] = useState(false);

  const [approvedForBooking, setApprovedForBooking] = useState<boolean>(company.approved_for_booking);
  const [approvedForBookingLoading, setApprovedForBookingLoading] = useState(false);

  useEffect(() => {
    setIsRelevant(company.is_relevant);
    setApprovedForBooking(company.approved_for_booking);
  }, [company]);

  const handleSwitchChange = async (field: string, value: boolean) => {
    const setLoading = field === "is_relevant" ? setIsRelevantLoading : setApprovedForBookingLoading;
    const setState = field === "is_relevant" ? setIsRelevant : setApprovedForBooking;

    setLoading(true);

    try {
      const response = await axios.patch(`/companies/${company.id}/`, {
        [field]: value,
      });

      if (response.status === 200) {
        setState(value);
        message(
          "success",
          `${field === "is_relevant" ? "Vises i bedriftskatalogen" : "Godkjent for booking"} oppdatert.`
        );
      } else {
        throw new Error("Noe gikk galt.");
      }
    } catch (error) {
      message("error", "Kunne ikke oppdatere. Prøv igjen senere.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="Detaljert informasjon" bordered style={{ width: "100%" }}>
      <InformationSwitch
        label="Vises i bedriftskatalogen:"
        checked={isRelevant}
        loading={isRelevantLoading}
        onChange={value => handleSwitchChange("is_relevant", value)}
      />
      <InformationSwitch
        label="Godkjent for booking:"
        checked={approvedForBooking}
        loading={approvedForBookingLoading}
        onChange={value => handleSwitchChange("approved_for_booking", value)}
      />
      <Divider />
      <Row style={{ marginBottom: 12 }}>
        <Col span={8}>
          <Text strong>Brukernavn:</Text>
        </Col>
        <Col span={16}>
          <Text strong>{company.username}</Text>
        </Col>
      </Row>
      <Divider />
      <Row style={{ marginBottom: 12 }}>
        <Col span={8}>
          <Text strong>Org.nr:</Text>
        </Col>
        <Col span={16}>
          <Text>{company.organization_number ?? ""}</Text>
        </Col>
      </Row>
      <Row style={{ marginBottom: 12 }}>
        <Col span={8}>
          <Text strong>Telefon:</Text>
        </Col>
        <Col span={16}>
          <Text>{company.telephone ?? ""}</Text>
        </Col>
      </Row>
      <Row style={{ marginBottom: 12 }}>
        <Col span={8}>
          <Text strong>Epost:</Text>
        </Col>
        <Col span={16}>
          <Text>{company.email ?? ""}</Text>
        </Col>
      </Row>
      <Row style={{ marginBottom: 12 }}>
        <Col span={8}>
          <Text strong>Nettside:</Text>
        </Col>
        <Col span={16}>
          <Text>{company.url ?? ""}</Text>
        </Col>
      </Row>
      <Row style={{ marginBottom: 12 }}>
        <Col span={8}>
          <Text strong>Adresse:</Text>
        </Col>
        <Col span={16}>
          <Text>{company.visitor_address ?? ""}</Text>
        </Col>
      </Row>
      <Row style={{ marginBottom: 12 }}>
        <Col span={8}>
          <Text strong>Postnummer:</Text>
        </Col>
        <Col span={16}>
          <Text>{company.visitor_area_code ?? ""}</Text>
        </Col>
      </Row>
      <Row style={{ marginBottom: 12 }}>
        <Col span={8}>
          <Text strong>Poststed:</Text>
        </Col>
        <Col span={16}>
          <Text>{company.visitor_region ?? ""}</Text>
        </Col>
      </Row>
    </Card>
  );
};

export default DetailedInformation;
